import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Result, Button } from 'antd'
import { Loader } from '@kolab/dms-base/components/ui'

/**
 * Queryable — the subset of UseQueryResult / useCursorList that this
 * component needs. Accepting a duck type means it works with both
 * useQuery, useInfiniteQuery wrappers, and useCursorList.
 */
interface Queryable<T> {
  isPending?: boolean
  isLoading?: boolean
  isError: boolean
  error: unknown
  data?: T
  refetch?: () => void
}

/**
 * Uniform loading / error / forbidden rendering for a page's primary query.
 *
 * Works with both UseQueryResult and useCursorList shapes. Picker queries
 * that only hydrate filter dropdowns should stay on the page with `?? []`
 * guards — this component is for the main data source.
 *
 * Why this exists: without it, a 403 (or any thrown ApiError from the axios
 * interceptor) leaves query.data === undefined; downstream `.items.length`
 * or destructuring blows up inside render and only the top-level
 * ErrorBoundary catches it, which wipes the content area. Handling the query
 * result state here keeps the shell intact and gives the user a meaningful
 * message instead of "Something went wrong".
 */
export function QueryStateView<T>({
  query,
  children,
}: {
  query: Queryable<T> | UseQueryResult<T>
  children: (data: T) => ReactNode
}) {
  if (query.isPending || query.isLoading) return <Loader />

  if (query.isError) {
    const err = query.error as { status?: number } | undefined
    if (err?.status === 403) {
      return (
        <Result
          status="403"
          title="Access denied"
          subTitle="You don't have permission to view this data."
        />
      )
    }
    return (
      <Result
        status="error"
        title="Unable to load"
        subTitle="Something went wrong while loading this data. Please try again."
        extra={
          query.refetch ? (
            <Button type="primary" onClick={() => query.refetch!()}>
              Try Again
            </Button>
          ) : undefined
        }
      />
    )
  }

  if (query.data === undefined) return <Loader />
  return <>{children(query.data)}</>
}

/**
 * Lightweight variant for useCursorList pages where the "data" is the
 * items array, not the query result. Checks isLoading/isError on the
 * cursor list return and renders the same error states.
 */
export function CursorListStateView({
  isLoading,
  isError,
  error,
  refetch,
  children,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch?: () => void
  children: ReactNode
}) {
  if (isLoading) return <Loader />

  if (isError) {
    const err = error as { status?: number } | undefined
    if (err?.status === 403) {
      return (
        <Result
          status="403"
          title="Access denied"
          subTitle="You don't have permission to view this data."
        />
      )
    }
    return (
      <Result
        status="error"
        title="Unable to load"
        subTitle="Something went wrong while loading this data. Please try again."
        extra={
          refetch ? (
            <Button type="primary" onClick={() => refetch()}>
              Try Again
            </Button>
          ) : undefined
        }
      />
    )
  }

  return <>{children}</>
}
